import {
  Box,
  Button,
  Chip,
  FormLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useEdges } from 'reactflow';
import { CompanySpacesService } from '../../service/CompanySpacesService';
import { useAuthStore } from '../../store/storeAuth';
import useFlowsStore from '../../store/storeFlows';
import { nodeDataAutoSaveDynamic } from '../../util/autosave/nodedata_autosave';
import { useDebounceEffect } from '../../util/useDebounceEffect';
import {
  apiSystemToCanonical,
  canonicalSystemDisplayNameMap,
  commandTextNodeDefaultValues,
} from '../AiConfigs/constants';
import Gemini from '../AiTextGenerator/Gemini';
import GroqAi from '../AiTextGenerator/GroqAi';
import OpenAi from '../AiTextGenerator/OpenAi';
import OpenSources from '../AiTextGenerator/OpenSources';

function DynamicTextSettingsNode({ id, data }: any) {
  const [formData, setFormData] = useState({
    ...commandTextNodeDefaultValues,
    ...data,
  });
  const [tokenCount, setTokenCount] = useState(0);
  const edges = useEdges();
  const { setNodeState, setSchema, setEdges, flowId, setSaving } =
    useFlowsStore(state => state);
  const [isChangingDirectly, setIsChangingDirectly] = useState(false);
  const [whoIsChanging, setWhoIsChanging] = useState({
    value: '',
    name: '',
  });

  const {
    user,
    technologies,
    companySpaceTechnologies,
    setCompanySpaceTechnologies,
  } = useAuthStore(state => state);
  const { spaceId } = useFlowsStore();

  const fetchAllTechnologies = async () => {
    try {
      const res = await CompanySpacesService.getSpaceTechnologies(
        spaceId as string,
      );
      setCompanySpaceTechnologies(res.data);
    } catch (error) {
      console.error('Error fetching company technologies:', error);
    }
    return;
  };

  useEffect(() => {
    if (spaceId) {
      fetchAllTechnologies();
    }
  }, [spaceId]);

  const textSystems = [
    'anthropic',
    'openai',
    'groq',
    'gemini',
    'perplexity',
    'opensources',
  ];

  const filteredTextSystems = spaceId
    ? companySpaceTechnologies
        ?.filter(system => textSystems?.includes(system?.system))
        ?.map(system => ({
          ...system,
          models: system?.models?.filter(
            (model: any) => model.type === 'Text' && !model.isDeprecated,
          ),
        }))
    : technologies
        ?.filter(system => textSystems.includes(system.system))
        ?.map(system => ({
          ...system,
          models: system?.models?.filter(
            (model: any) => model.type === 'Text' && !model.isDeprecated,
          ),
        }));

  useEffect(() => {
    const systemsToUse =
      spaceId && companySpaceTechnologies.length > 0
        ? companySpaceTechnologies
        : technologies;

    if (!systemsToUse || systemsToUse.length === 0) return;
    if (!formData.aiSystem || !formData.aiModel) return;

    const apiSystem = Object.entries(apiSystemToCanonical).find(
      ([, canonical]) => canonical === formData.aiSystem,
    )?.[0];
    if (!apiSystem) return;

    const system = systemsToUse.find((s: any) => s.system === apiSystem);
    if (!system) return;

    const availableModels = system.models?.filter(
      (m: any) => m.type === 'Text' && !m.isDeprecated,
    );
    if (!availableModels || availableModels.length === 0) return;

    const isCurrentValid = availableModels.some(
      (m: any) => m.model === formData.aiModel,
    );
    if (isCurrentValid) return;

    const fallback =
      availableModels.find((m: any) => m.isDefault) || availableModels[0];
    if (!fallback) return;

    onChange({ target: { name: 'aiModel', value: fallback.model } });
  }, [
    technologies,
    companySpaceTechnologies,
    spaceId,
    formData.aiSystem,
    formData.aiModel,
  ]);

  const setValue = (name: string, value: string) => {
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const onChange = (e: { target: { name: any; value: any } }) => {
    setValue(e.target.name, e.target.value);

    setWhoIsChanging({
      value: e.target.value,
      name: e.target.name,
    });

    setIsChangingDirectly(true);
  };

  useDebounceEffect(
    () => {
      if (isChangingDirectly) {
        setIsChangingDirectly(false);
        nodeDataAutoSaveDynamic({
          setEdges,
          setNodeState,
          newEdges: edges,
          id,
          setSaving,
          changeType: 'execute',
          flowId,
          objectData: {
            [whoIsChanging.name]: whoIsChanging.value,
          },
          setSchema,
        });

        setWhoIsChanging({
          value: '',
          name: '',
        });
      }
    },
    [isChangingDirectly, whoIsChanging],
    300,
  );

  useEffect(() => {
    if (data && !isChangingDirectly) {
      setFormData({
        ...commandTextNodeDefaultValues,
        ...data,
      });
    }
  }, [data, isChangingDirectly]);

  const sendData = (e: any) => {
    // e.preventDefault();
    // console.log(formData)
  };

  const TestTest = useMemo(() => {
    const aiSystem = formData?.aiSystem;
    const aiModel = formData?.aiModel;
    if (aiSystem === 'opensources') {
      return JSON.stringify({
        aiSystem,
        aiModel,
        MaxLength: formData?.max_length,
        Temperature: formData?.temperature,
        TopP: formData?.top_p,
        RepetitionPenalty: formData?.repetition_penalty,
        LengthPenalty: formData?.length_penalty,
        NoRepeatNgramSize: formData?.no_repeat_ngram_size,
        Seed: formData?.seed,
      });
    }
    if (aiSystem === 'gemini') {
      return JSON.stringify({
        aiSystem,
        aiModel,
        Tokens: formData?.maxOutputTokens,
        Temperature: formData?.temperature,
        TopP: formData?.topP,
        TopK: formData?.topK,
      });
    }
    if (aiSystem === 'anthropic') {
      return JSON.stringify({
        aiSystem,
        aiModel,
        Tokens: formData?.maxOutputTokens,
      });
    }
    if (aiSystem === 'perplexity') {
      return JSON.stringify({ aiSystem, aiModel });
    }
    return JSON.stringify({
      aiSystem,
      aiModel,
      Temperature: formData?.aiTemperature,
      PPenalty: formData?.aiPp,
      FPenalty: formData?.aiFp,
      Tokens: formData?.aiTokens,
    });
  }, [
    formData?.aiSystem,
    formData?.aiModel,
    formData?.max_length,
    formData?.temperature,
    formData?.top_p,
    formData?.repetition_penalty,
    formData?.length_penalty,
    formData?.no_repeat_ngram_size,
    formData?.seed,
    formData?.modelName,
    formData?.topP,
    formData?.topK,
    formData?.maxOutputTokens,
    formData?.aiTemperature,
    formData?.aiPp,
    formData?.aiFp,
    formData?.aiTokens,
  ]);

  return (
    <>
      {!data?.collapsed && (
        <>
          <form>
            <Box className="content">
              <Box
              //   style={modelInputBoxStyle}
              >
                <Typography
                  color={user?.theme === 'dark' ? '#D0D5DD' : 'black'}
                >
                  System
                </Typography>
                <Select
                  className="nodrag"
                  defaultValue={'openai'}
                  name="aiSystem"
                  onChange={onChange}
                  value={data?.aiSystem}
                  sx={{
                    marginLeft: '8px',
                    '& .MuiList-root': {
                      backgroundColor:
                        user?.theme === 'dark' ? '#101828' : '#F9FAFB',
                    },
                    '& .MuiSvgIcon-root': {
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    },
                    borderRadius: '8px',
                    border: 'none',
                    boxShadow: 'none',
                    '.MuiOutlinedInput-notchedOutline': { border: 0 },
                    bgcolor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        '& .Mui-selected': {
                          backgroundColor:
                            user?.theme === 'dark' ? '#667085' : '#D0D5DD',
                        },
                        backgroundColor:
                          user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                      },
                    },
                  }}
                >
                  {filteredTextSystems?.map((opt, index) => {
                    const disabled = opt?.models?.every(
                      (model: any) => model.enabled === false,
                    );
                    const disabledByCompany = opt?.models?.every(
                      (model: any) => model.disabledByCompany,
                    );
                    const canonicalValue =
                      apiSystemToCanonical[opt.system] || opt.system;
                    const displayName =
                      canonicalSystemDisplayNameMap[
                        canonicalValue as keyof typeof canonicalSystemDisplayNameMap
                      ] || canonicalValue;
                    return (
                      <MenuItem
                        key={index}
                        value={canonicalValue}
                        disabled={disabled}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            width: '100%',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Typography>{displayName}</Typography>
                          {disabledByCompany && (
                            <Chip
                              label="Disabled by admin"
                              color="error"
                              size="small"
                              variant="filled"
                              sx={{
                                borderRadius: '8px',
                              }}
                            />
                          )}
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </Box>
              {formData.aiSystem === 'openai' && (
                <OpenAi
                  watch={formData}
                  onChange={onChange}
                  systems={filteredTextSystems}
                  // setIsChangingDirectly={setIsChangingDirectly}
                  tokenCount={tokenCount}
                />
              )}

              {formData.aiSystem === 'groq' && (
                <GroqAi onChange={onChange} watch={formData} />
              )}

              {formData.aiSystem === 'perplexity' && (
                <Box>
                  <FormLabel
                    sx={{
                      mb: '8px',
                      mt: '8px',
                      color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                    }}
                  >
                    Type
                  </FormLabel>
                  <Select
                    className="nodrag"
                    value={formData.aiModel}
                    fullWidth
                    size="small"
                    onChange={onChange}
                    name={'aiModel'}
                    sx={{
                      '& .MuiList-root': {
                        backgroundColor:
                          user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                      },
                      bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                      border: `2px solid ${
                        user?.theme === 'dark' ? '#475467' : '#EAECF0'
                      }`,
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          '& .Mui-selected': {
                            backgroundColor:
                              user?.theme === 'dark' ? '#667085' : '#D0D5DD',
                          },
                          backgroundColor:
                            user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                        },
                      },
                    }}
                  >
                    {filteredTextSystems
                      ?.filter((item: any) => item.system === 'perplexity')?.[0]
                      ?.models?.map((opt: any) => (
                        <MenuItem
                          key={opt.id}
                          value={opt.model}
                          disabled={opt.disabledByCompany}
                        >
                          {opt.model}
                        </MenuItem>
                      ))}
                  </Select>
                </Box>
              )}

              {formData.aiSystem === 'anthropic' && (
                <>
                  <Box>
                    <FormLabel
                      sx={{
                        mb: '4px',
                        bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                        color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                      }}
                    >
                      Model
                    </FormLabel>

                    <Select
                      className="nodrag"
                      value={formData.aiModel}
                      size="small"
                      fullWidth
                      onChange={onChange}
                      name={'aiModel'}
                      sx={{
                        '& .MuiList-root': {
                          backgroundColor:
                            user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                        },
                        bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                        color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                        border: `2px solid ${
                          user?.theme === 'dark' ? '#475467' : '#EAECF0'
                        }`,
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            '& .Mui-selected': {
                              backgroundColor:
                                user?.theme === 'dark' ? '#667085' : '#D0D5DD',
                            },
                            backgroundColor:
                              user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                          },
                        },
                      }}
                    >
                      {filteredTextSystems
                        ?.filter(
                          (item: any) => item.system === 'anthropic',
                        )?.[0]
                        .models?.map((opt: any) => (
                          <MenuItem
                            key={opt.id}
                            value={opt.model}
                            disabled={opt.disabledByCompany}
                          >
                            {opt.model}
                          </MenuItem>
                        ))}
                    </Select>
                  </Box>

                  <Box
                    sx={{
                      mt: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <FormLabel
                      sx={{
                        mb: '4px',
                        color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                      }}
                    >
                      Max Output
                    </FormLabel>
                    <TextField
                      type={'number'}
                      onChange={e => {
                        onChange(e);
                      }}
                      value={formData.max_tokens}
                      name={'max_tokens'}
                      style={{ marginLeft: '8px', width: '80px' }}
                      className="nodrag"
                      InputProps={{
                        style: {
                          color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                        },
                      }}
                      sx={{
                        color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                        bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                        border: `2px solid ${
                          user?.theme === 'dark' ? '#475467' : '#EAECF0'
                        }`,
                      }}
                    />
                  </Box>
                </>
              )}

              {formData.aiSystem === 'gemini' && (
                <Gemini
                  imageSystem
                  watch={formData}
                  onChange={onChange}
                  systems={filteredTextSystems}
                />
              )}

              {formData.aiSystem === 'opensources' && (
                <OpenSources watch={formData} onChange={onChange} />
              )}
            </Box>
            <TextField
              className="nodrag"
              disabled
              value={TestTest}
              multiline
              InputProps={{
                style: {
                  color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                },
              }}
              sx={{
                color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                border: `2px solid ${
                  user?.theme === 'dark' ? '#475467' : '#EAECF0'
                }`,
              }}
            />
            <Button> Send Data</Button>
          </form>
        </>
      )}
    </>
  );
}

export default DynamicTextSettingsNode;
